import axios, { AxiosResponse } from "axios";
import { currentShippingScanItemAtom, IShippingScanRequest, IShippingScanResponse, orderShippingInfoAtom, shippingScanLoading } from "./types";
import { environment } from "../../environment";
import { useLogin } from "../login";
import { errorCatcher, useSnackbar } from "../../hooks";
import { useAtom } from "jotai";
import { IPrinter } from "../printers";


type ScanItem = (query: IShippingScanRequest) => Promise<IShippingScanResponse | null>;
type VoidLabel = (shipmentId: number) => Promise<IShippingScanResponse | null>;
type GetShippingInfo = (orderId: number) => Promise<IShippingScanResponse | null>;
export type ReprintLabel = (shipmentId: number, printer: IPrinter) => Promise<boolean>;

type UseShippingKeyFunctions = {
    scan: ScanItem,
    voidLabel: VoidLabel,
    getShippingInfo: GetShippingInfo,
    reprintLabel: ReprintLabel
}

type UseShipping = [
    IShippingScanResponse | null,
    UseShippingKeyFunctions
];

/**
 * The order-handler returns `{ message, status }` on every handled failure. The
 * shared errorCatcher throws that away, so operators saw one generic string for
 * everything — a 409 "these boxes were added after confirm" was indistinguishable
 * from a 500. Pull the real message out so the station can act on it.
 */
const serverMessage = (err: any, fallback: string): string => {
    const data = err?.response?.data;
    if (typeof data?.message === 'string' && data.message.length > 0) return data.message;
    return fallback;
};

/** Client errors (4xx) are actionable states, not crashes — keep the panel up. */
const isClientError = (err: any): boolean => {
    const status = err?.response?.status;
    return typeof status === 'number' && status >= 400 && status < 500;
};

export const useShipping = (): UseShipping => {
    const [employee, authToken, loginFunctions] = useLogin();
    const [, setSnackbar] = useSnackbar();
    const [scan, setScan] = useAtom(currentShippingScanItemAtom);
    const [, setScanLoading] = useAtom(shippingScanLoading);
    const [, setOrderShippingInfo] = useAtom(orderShippingInfoAtom);

    const getShippingInfo: GetShippingInfo = async (orderId: number): Promise<IShippingScanResponse | null> => {
        const scanResponse: AxiosResponse<IShippingScanResponse> | void = await axios.get(`${environment.shippingUrl}/get-shipping-info/${orderId}`, {
            headers: {
                'X-Wallmates-Auth': authToken
            }
        }).catch((err: any) => errorCatcher(err, loginFunctions.logout));

        if (scanResponse) {
            setOrderShippingInfo(scanResponse.data);
            return scanResponse.data;
        } else {
            return null;
        }
    }

    const scanItem: ScanItem = async (query: IShippingScanRequest): Promise<IShippingScanResponse | null> => {
        if (employee !== null) {
            let scanError: any = null;
            const scanResponse: AxiosResponse<IShippingScanResponse> | void = await axios.post(`${environment.shippingUrl}/scan-item`, { ...query, employee: employee.id }, {
                headers: {
                    'X-Wallmates-Auth': authToken
                }
            }).catch((err: any) => { scanError = err; errorCatcher(err, loginFunctions.logout); });

            if (scanResponse) {
                setScan(scanResponse.data);
                return scanResponse.data;
            } else {
                setSnackbar({
                    show: true,
                    snackbarLevel: 'error',
                    text: serverMessage(scanError, 'There was an error while performing box scan')
                })
                // A 4xx means the scan was understood and refused for a reason the
                // operator can fix (e.g. ungrouped boxes needing a re-confirm).
                // Blanking the panel would hide the very state they need to see.
                if (!isClientError(scanError)) setScan(null);
                setScanLoading(false);
                return null;
            }
        } else {
            loginFunctions.logout();
            return null;
        }
    }

    const voidLabel: VoidLabel = async (shipmentId: number): Promise<IShippingScanResponse | null> => {
        if (employee !== null) {
            let voidError: any = null;
            const scanResponse: AxiosResponse<IShippingScanResponse> | void = await axios.post(`${environment.shippingUrl}/void-label`, { shipmentId }, {
                headers: {
                    'X-Wallmates-Auth': authToken
                }
            }).catch((err: any) => { voidError = err; errorCatcher(err, loginFunctions.logout); });

            if (scanResponse) {
                setScan(scanResponse.data);
                // Keep the order page in sync — voiding from the packing station's
                // order snapshot used to leave the Order view showing a live label.
                setOrderShippingInfo(scanResponse.data);
                setSnackbar({
                    show: true,
                    snackbarLevel: 'success',
                    text: 'Shipping label voided. It stays on the order marked as voided.'
                })
                return scanResponse.data;
            } else {
                setSnackbar({
                    show: true,
                    snackbarLevel: 'error',
                    text: serverMessage(voidError, 'There was an error while voiding the label')
                })
                if (!isClientError(voidError)) setScan(null);
                setScanLoading(false);
                return null;
            }
        } else {
            loginFunctions.logout();
            return null;
        }
    }

    const reprintLabel: ReprintLabel = async (shipmentId: number, printer: IPrinter): Promise<boolean> => {
        if (employee === null) {
            loginFunctions.logout();
            return false;
        }

        const reprintResponse: AxiosResponse | void = await axios.post(`${environment.shippingUrl}/reprint-label`, { shipmentId, printer }, {
            headers: {
                'X-Wallmates-Auth': authToken
            }
        }).catch((err: any) => errorCatcher(err, loginFunctions.logout));

        if (reprintResponse) {
            setSnackbar({
                show: true,
                snackbarLevel: 'success',
                text: 'Shipping label sent to printer'
            });
            return true;
        }

        setSnackbar({
            show: true,
            snackbarLevel: 'error',
            text: 'There was an error while reprinting the label'
        });
        return false;
    }

    const shippingKeyFunctions: UseShippingKeyFunctions = {
        scan: scanItem,
        voidLabel: voidLabel,
        getShippingInfo: getShippingInfo,
        reprintLabel: reprintLabel
    }

    return [
        scan,
        shippingKeyFunctions
    ]
}